import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import InnerLayout from "../../../components/layouts/inner-layout";
import AddButton, { DraftButton } from "../../../components/buttons/button";
import CustomReportTable from "../../../components/custom-reports/custom-report-table";
import ExportDropdown from "../../../components/custom-reports/export-dropdown";
import {
  customReportGetAction,
  customReportExecuteAction,
} from "../../../redux/actions/custom-report-action";
import { PencilIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { canCreateAndEditCustomReports } from "../../../utils/custom-report-permissions";

const CustomReportView = () => {
  const canEdit = canCreateAndEditCustomReports();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportId } = useParams();

  const [report, setReport] = useState(null);
  const [results, setResults] = useState({ columns: [], rows: [], rowCount: 0, truncated: false });
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reportId) {
      fetchReportAndExecute(reportId);
    }
  }, [reportId]);

  const fetchReportAndExecute = (id) => {
    setLoading(true);
    setError(null);
    dispatch(
      customReportGetAction(
        id,
        (response) => {
          const reportData = response.data;
          setReport(reportData);
          executeQuery(reportData.sql_query);
        },
        (error) => {
          console.error("Failed to fetch report:", error);
          setError(error?.data?.message || error?.message || "Failed to load report");
          setLoading(false);
        }
      )
    );
  };

  const executeQuery = (sqlQuery) => {
    setExecuting(true);
    setError(null);
    dispatch(
      customReportExecuteAction(
        { sql_query: sqlQuery },
        (response) => {
          setResults(response.data);
          setLoading(false);
          setExecuting(false);
        },
        (errorResponse) => {
          setError(errorResponse?.data?.message || errorResponse?.message || "Query execution failed");
          setLoading(false);
          setExecuting(false);
        }
      )
    );
  };

  const handleRefresh = () => {
    if (report?.sql_query) {
      setExecuting(true);
      setError(null);
      setResults({ columns: [], rows: [], rowCount: 0, truncated: false });
      executeQuery(report.sql_query);
    }
  };

  const handleEdit = () => {
    navigate(`/reports/custom-reports/editor/${reportId}`);
  };

  if (loading && !report) {
    return (
      <InnerLayout title="Loading..." backBtn={true} list={true}>
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      </InnerLayout>
    );
  }

  if (error && !report) {
    return (
      <InnerLayout title="Error" backBtn={true} list={true}>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-120px)]">
          <p className="text-F94B4B mb-4">{error}</p>
          <DraftButton text="Go Back" onclkFn={() => navigate("/reports/custom-reports")} />
        </div>
      </InnerLayout>
    );
  }

  return (
    <InnerLayout
      title={report?.name || "Custom Report"}
      backBtn={true}
      list={true}
    >
      <div className="flex flex-col h-[calc(100vh-120px)]">
        {/* Header with actions */}
        <div className="px-6 py-4 border-b border-EAECF0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg fw_600 text-344054">{report?.name}</h2>
              {report?.description && (
                <p className="text-sm text-667085 mt-1">{report.description}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <DraftButton
                text="Refresh"
                icon={<ArrowPathIcon className={`w-4 h-4 ${executing ? 'animate-spin' : ''}`} />}
                onclkFn={handleRefresh}
                isDisabled={executing}
              />
              {canEdit && (
                <AddButton
                  text="Edit"
                  icon={<PencilIcon className="w-4 h-4" />}
                  onclkFn={handleEdit}
                />
              )}
              <ExportDropdown
                data={results.rows}
                columns={results.columns}
                reportName={report?.name || "Custom Report"}
                disabled={results.rows.length === 0 || executing}
              />
            </div>
          </div>
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-F94B4B rounded-md">
              <p className="text-xs text-F51D00">{error}</p>
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="flex-1 overflow-hidden">
          {executing ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-sm text-667085">Running query...</p>
              </div>
            </div>
          ) : (
            <CustomReportTable
              columns={results.columns}
              rows={results.rows}
              rowCount={results.rowCount}
              truncated={results.truncated}
            />
          )}
        </div>
      </div>
    </InnerLayout>
  );
};

export default CustomReportView;
